import { useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { Mail, CheckCircle, LogOut, AlertCircle } from 'lucide-react'
import logger from '../utils/logger'
import logoFull from '../assets/logo-full.png'

/**
 * Full-screen gate shown to new (post-cutoff) users who haven't verified their email.
 * User enters a 6-digit OTP code from their verification email.
 */
export default function EmailVerificationGate() {
  const { user, signOut } = useAuth()
  const OTP_LENGTH = 6
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(''))
  const [error, setError] = useState(null)
  const [verifying, setVerifying] = useState(false)
  const [resent, setResent] = useState(false)
  const [sending, setSending] = useState(false)
  const inputRefs = useRef([])

  const handleChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1)
    const newCode = [...code]
    newCode[index] = digit
    setCode(newCode)
    setError(null)

    // Auto-advance to next input
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits are entered
    if (digit && index === OTP_LENGTH - 1 && newCode.every(d => d)) {
      handleVerify(newCode.join(''))
    }
  }

  const handleKeyDown = (index, e) => {
    // Backspace moves to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const newCode = [...code]
    for (let i = 0; i < OTP_LENGTH; i++) {
      newCode[i] = pasted[i] || ''
    }
    setCode(newCode)
    setError(null)

    // Focus last filled input or the next empty one
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[focusIndex]?.focus()

    // Auto-submit if all 6 digits pasted
    if (pasted.length === OTP_LENGTH) {
      handleVerify(pasted)
    }
  }

  const handleVerify = async (token) => {
    setVerifying(true)
    setError(null)
    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: user.email,
      token,
      type: 'email',
    })
    if (verifyError) {
      logger.capture(verifyError, { source: 'EmailVerificationGate', action: 'verifyOtp' })
      setError('Invalid or expired code. Please try again.')
      setCode(Array(OTP_LENGTH).fill(''))
      inputRefs.current[0]?.focus()
    }
    // On success, Supabase updates the session and AuthContext picks it up
    setVerifying(false)
  }

  const handleResend = async () => {
    setSending(true)
    setError(null)
    await supabase.auth.resend({ type: 'signup', email: user.email })
    setResent(true)
    setSending(false)
    // Reset after 30 seconds so they can resend again
    setTimeout(() => setResent(false), 30000)
  }

  const inputStyle = {
    width: '44px',
    height: '52px',
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: '700',
    borderRadius: '0.5rem',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-tertiary)',
    color: 'var(--text-primary)',
    outline: 'none',
    caretColor: 'var(--accent-primary)',
  }

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'calc(2rem + env(safe-area-inset-top, 0px)) 1.25rem 2rem',
      backgroundColor: 'var(--bg-primary)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
      }}>
        <img src={logoFull} alt="Press2DN" style={{ maxWidth: '160px', width: '100%', height: 'auto', marginBottom: '1.5rem' }} />

        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          padding: '2rem 1.5rem',
          border: '1px solid var(--border-color)',
        }}>
          <Mail size={40} style={{ color: 'var(--accent-primary)', marginBottom: '1rem' }} />

          <h2 style={{
            color: 'var(--text-primary)',
            fontSize: '1.25rem',
            fontWeight: '700',
            marginBottom: '0.5rem',
          }}>
            Verify your email
          </h2>

          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            marginBottom: '1.5rem',
          }}>
            Enter the code sent to <strong style={{ color: 'var(--text-primary)' }}>{user?.email}</strong>
          </p>

          {/* OTP Input */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1.25rem',
          }}>
            {code.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                onPaste={i === 0 ? handlePaste : undefined}
                disabled={verifying}
                autoFocus={i === 0}
                style={{
                  ...inputStyle,
                  borderColor: error ? '#ef4444' : digit ? 'var(--accent-primary)' : 'var(--border-color)',
                  opacity: verifying ? 0.7 : 1,
                }}
              />
            ))}
          </div>

          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              color: '#ef4444',
              fontSize: '0.8125rem',
              marginBottom: '1rem',
            }}>
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {verifying && (
            <p style={{
              color: 'var(--text-muted)',
              fontSize: '0.8125rem',
              marginBottom: '1rem',
            }}>
              Verifying...
            </p>
          )}

          <div style={{ marginTop: '0.5rem' }}>
            {resent ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                color: 'var(--accent-primary)',
                fontSize: '0.8125rem',
                fontWeight: '600',
              }}>
                <CheckCircle size={14} />
                <span>New code sent</span>
              </div>
            ) : (
              <button
                onClick={handleResend}
                disabled={sending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  fontWeight: 600,
                  cursor: sending ? 'wait' : 'pointer',
                  fontSize: '0.8125rem',
                  padding: 0,
                }}
              >
                {sending ? 'Sending...' : "Didn't get it? Resend code"}
              </button>
            )}
          </div>

          <button
            onClick={signOut}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: '0.8125rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              width: '100%',
              padding: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>

        <p style={{
          marginTop: '1.5rem',
          fontSize: '0.8125rem',
          color: 'var(--text-muted)',
          lineHeight: 1.5,
        }}>
          Check your spam folder if you don't see the email.
        </p>
      </div>
    </div>
  )
}
